import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Field, SideDrawer, Text, TextField } from "components/commons";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { ConfirmModal } from "components/modals";
import { useAlert, useApi, useModal } from "hooks";
import { updateShiftById } from "apis/shift-records.api";
import locale from "localization";
import { prettifyPaymentMethod } from "utils/pretty.utils";
import {
  adjustmentPaymentMethodOptions,
  fuelOnlyAdjustmentPaymentMethods,
  isNonCashAdjustmentPaymentMethod,
  lubesOnlyAdjustmentPaymentMethods,
  resolveAdjustmentPaymentMethod,
} from "data/constant/adjust-payment-method";
import styles from "./adjust-payment-method-drawer.module.scss";

const REF_NUMBER_MAX_LENGTH = 25;
const DISALLOWED_REF_NUMBER_CHARS = /[<>]/g;

const AdjustPaymentMethodDrawer = ({
  transaction,
  shiftId,
  allPaymentMethodRows = [],
  transactionsItemDetails = [],
  onUpdatePaymentMethod,
  ...drawer
}) => {
  const { addAlert } = useAlert();
  const discardModal = useModal();

  const {
    transactionPaymentMethodId,
    transactionId,
    method: currentMethod,
    refNumber: currentRefNumber,
  } = transaction ?? {};

  const isSplitPayment = useMemo(() => {
    if (!transactionId) return false;
    const paymentMethods =
      transactionsItemDetails.find((item) => item.transactionId === transactionId)?.transaction
        ?.paymentMethods || [];
    return new Set(paymentMethods.map((pm) => pm.transactionPaymentMethodId)).size > 1;
  }, [transactionId, transactionsItemDetails]);

  const resolvedCurrentMethod = useMemo(
    () => resolveAdjustmentPaymentMethod(currentMethod),
    [currentMethod],
  );

  const restrictedMethods = useMemo(() => {
    if (!transactionId) return [];
    const productTypes = [...allPaymentMethodRows, ...transactionsItemDetails]
      .filter((item) => item.transactionId === transactionId && item.type)
      .map((item) => item.type);
    return [
      ...(productTypes.some((type) => type === "fuel") ? lubesOnlyAdjustmentPaymentMethods : []),
      ...(productTypes.some((type) => type !== "fuel") ? fuelOnlyAdjustmentPaymentMethods : []),
    ];
  }, [transactionId, allPaymentMethodRows, transactionsItemDetails]);

  const methodOptions = useMemo(
    () =>
      adjustmentPaymentMethodOptions.filter(
        ({ value }) => value === resolvedCurrentMethod || !restrictedMethods.includes(value),
      ),
    [restrictedMethods, resolvedCurrentMethod],
  );

  const [newMethod, setNewMethod] = useState("");
  const [refNumber, setRefNumber] = useState("");
  const [refNumberTouched, setRefNumberTouched] = useState(false);

  useEffect(() => {
    if (!drawer.active) return;
    setNewMethod(resolvedCurrentMethod);
    setRefNumber(currentRefNumber ?? "");
    setRefNumberTouched(false);
  }, [drawer.active, resolvedCurrentMethod, currentRefNumber]);

  const updateShift = useApi({
    api: updateShiftById,
    handleOwnError: true,
    params: {
      shiftId,
    },
  });

  const affectedRowCount = useMemo(() => {
    if (!transactionPaymentMethodId) return 0;
    const count = allPaymentMethodRows.filter(
      (row) => row.transactionPaymentMethodId === transactionPaymentMethodId,
    ).length;
    return count > 1 ? count - 1 : 0;
  }, [allPaymentMethodRows, transactionPaymentMethodId]);

  const hasChange = !isSplitPayment && Boolean(newMethod) && newMethod !== resolvedCurrentMethod;
  const requiresRefNumber = hasChange && isNonCashAdjustmentPaymentMethod(newMethod);
  const trimmedRefNumber = refNumber.trim();
  const hasRefNumberError = requiresRefNumber && !trimmedRefNumber;

  const canSave =
    hasChange &&
    Boolean(transactionPaymentMethodId) &&
    !hasRefNumberError &&
    !updateShift.loading;

  const handleClose = () => {
    if (hasChange && !updateShift.loading) {
      discardModal.show();
      return;
    }
    drawer.close?.();
  };

  const onChangeMethod = useCallback(
    (value) => {
      setNewMethod(value);
      if (value === resolvedCurrentMethod) {
        setRefNumber(currentRefNumber ?? "");
        setRefNumberTouched(false);
        return;
      }
      if (!isNonCashAdjustmentPaymentMethod(value)) {
        setRefNumber("");
        setRefNumberTouched(false);
      }
    },
    [resolvedCurrentMethod, currentRefNumber],
  );

  const handleSave = useCallback(async () => {
    try {
      const savedRefNumber = requiresRefNumber ? trimmedRefNumber : "";

      await updateShift.request({
        paymentMethod: {
          transactionPaymentMethodId,
          method: newMethod,
          refNumber: savedRefNumber,
        },
      });

      onUpdatePaymentMethod?.(transactionPaymentMethodId, {
        method: newMethod,
        refNumber: savedRefNumber,
      });

      drawer.close?.();
      addAlert({
        title: locale.successfullyUpdated,
        subtitle: locale.yourChangesHaveBeenSaved,
        type: "success",
      });
    } catch (error) {
      drawer.close?.();

      return addAlert({
        title: locale.errorWhileUpdatingPaymentMethod,
        subtitle: error?.data?.message || error?.message || "Unknown error occurred",
        type: "error",
        interval: 10000,
      });
    }
  }, [
    updateShift,
    transactionPaymentMethodId,
    newMethod,
    requiresRefNumber,
    trimmedRefNumber,
    onUpdatePaymentMethod,
    drawer,
    addAlert,
  ]);

  return (
    <>
      <SideDrawer
        {...drawer}
        close={handleClose}
        title={locale.adjustPaymentMethod}
      >
        <Text className={styles.description}>
          {locale.bosReportingCorrection}{" "}
          {isSplitPayment ? (
            locale.paymentMethodChangeUnavailableForSplitPayment
          ) : (
            <locale.Populate
              text={locale.changesAlsoApplyToOtherRows}
              items={[<b key="rows">{affectedRowCount} {locale.otherRow}</b>]}
            />
          )}
        </Text>

        <div className={styles.methodRow}>
          <Field label={locale.currentPaymentMethod} className={styles.methodField}>
            <div className={styles.readOnlyBox}>
              {currentMethod ? prettifyPaymentMethod(currentMethod) : "--"}
            </div>
          </Field>
          <ArrowForwardIcon className={styles.methodArrow} fontSize="small" />
          <Field label={locale.newPaymentMethod} className={styles.methodField}>
            <Dropdown
              name="newPaymentMethod"
              value={newMethod || "none"}
              placeholder={locale.selectPayment}
              options={methodOptions}
              disabled={isSplitPayment}
              onChange={(value) => onChangeMethod(value)}
            />
          </Field>
        </div>

        <Field
          label={locale.referenceNumber}
          className={styles.field}
          error={refNumberTouched && hasRefNumberError}
          message={
            refNumberTouched && hasRefNumberError ? locale.pleaseEnterAReferenceNumber : undefined
          }
        >
          <TextField
            name="refNumber"
            value={refNumber}
            placeholder="ABCD01234"
            maxLength={REF_NUMBER_MAX_LENGTH}
            disabled={!requiresRefNumber}
            inputDisabled={!requiresRefNumber}
            error={refNumberTouched && hasRefNumberError}
            onChange={(_, { value }) =>
              setRefNumber(value.replace(DISALLOWED_REF_NUMBER_CHARS, ""))
            }
            onBlur={() => setRefNumberTouched(true)}
          />
        </Field>

        <div className={styles.actions}>
          <Button outline onClick={handleClose}>
            {locale.cancel}
          </Button>
          <Button primary disabled={!canSave} loading={updateShift.loading} onClick={handleSave}>
            {locale.saveAdjustment}
          </Button>
        </div>
      </SideDrawer>
      <ConfirmModal
        {...discardModal}
        containerStyle={styles.discardModal}
        title={locale.discardChangesQuestion}
        content={locale.discardChangesMessage}
        secondary={{ text: locale.no }}
        primary={{
          text: locale.yes,
          onClick: () => {
            discardModal.close();
            drawer.close?.();
          },
        }}
      />
    </>
  );
};

export default AdjustPaymentMethodDrawer;
